import React, { useCallback } from 'react';
import { Zap } from 'lucide-react';
import type { ElectricalPanelSection } from '@officehub/shared';
import { INPUT_CLASS, LABEL_CLASS, TEXTAREA_CLASS } from './styles';
import type { RenderSectionPhotoCapture } from './photoCapture';

export interface ElectricalPanelFormProps {
  data: ElectricalPanelSection;
  onUpdate: (d: Partial<ElectricalPanelSection>) => void;
  /**
   * Optional host-supplied photo capture panel. When omitted the Photos block
   * is not rendered (structured-data-only capture).
   */
  renderPhotoCapture?: RenderSectionPhotoCapture;
}

export function ElectricalPanelForm({
  data,
  onUpdate,
  renderPhotoCapture,
}: ElectricalPanelFormProps): React.ReactElement {
  // A cleared optional input emits null, never undefined. survey_updateSection
  // merges the fields it is given (issue #3230), so only an explicit null
  // deletes a field — an absent key just leaves the stored value alone. The
  // callable SDK happens to encode undefined as null on the wire, but that is
  // an implementation detail of @firebase/functions; saying null here states
  // the intent at the boundary instead of depending on the transport for it.
  const handleChange = useCallback(
    (field: keyof ElectricalPanelSection, value: unknown) => {
      onUpdate({ [field]: value });
    },
    [onUpdate],
  );

  return (
    <div className="flex flex-col gap-6">
      {/* Section header */}
      <div className="flex items-center gap-2 px-1">
        <Zap className="w-3.5 h-3.5 text-slate-400" />
        <h2 className="text-[11px] font-black text-slate-500 uppercase tracking-[0.1em]">
          Electrical Panel
        </h2>
        <div className="flex-1 h-px bg-gradient-to-r from-slate-200 to-transparent" />
      </div>

      {/* 2-col grid fields */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Brand */}
        <div>
          <label className={LABEL_CLASS}>Brand</label>
          <input
            data-testid="field-brand"
            type="text"
            className={INPUT_CLASS}
            value={data.brand ?? ''}
            onChange={(e) => handleChange('brand', e.target.value)}
            placeholder="e.g., Square D, Siemens"
          />
        </div>

        {/* Main Breaker Amps */}
        <div>
          <label className={LABEL_CLASS}>Main Breaker (A)</label>
          <input
            data-testid="field-mainBreakerAmps"
            type="number"
            inputMode="numeric"
            className={INPUT_CLASS}
            value={data.mainBreakerAmps ?? ''}
            onChange={(e) =>
              handleChange('mainBreakerAmps', e.target.value === '' ? null : Number(e.target.value))
            }
            placeholder="e.g., 200"
          />
        </div>

        {/* Bus Rating */}
        <div>
          <label className={LABEL_CLASS}>Bus Rating (A)</label>
          <input
            data-testid="field-busRating"
            type="number"
            inputMode="numeric"
            className={INPUT_CLASS}
            value={data.busRating ?? ''}
            onChange={(e) =>
              handleChange('busRating', e.target.value === '' ? null : Number(e.target.value))
            }
            placeholder="e.g., 200"
          />
        </div>

        {/* Total Spaces */}
        <div>
          <label className={LABEL_CLASS}>Total Spaces</label>
          <input
            data-testid="field-totalSpaces"
            type="number"
            inputMode="numeric"
            className={INPUT_CLASS}
            value={data.totalSpaces ?? ''}
            onChange={(e) =>
              handleChange('totalSpaces', e.target.value === '' ? null : Number(e.target.value))
            }
            placeholder="e.g., 40"
          />
        </div>

        {/* Available Spaces */}
        <div>
          <label className={LABEL_CLASS}>Available Spaces</label>
          <input
            data-testid="field-availableSpaces"
            type="number"
            inputMode="numeric"
            className={INPUT_CLASS}
            value={data.availableSpaces ?? ''}
            onChange={(e) =>
              handleChange('availableSpaces', e.target.value === '' ? null : Number(e.target.value))
            }
            placeholder="e.g., 6"
          />
        </div>

        {/* Distance to Generator Pad */}
        <div>
          <label className={LABEL_CLASS}>Distance to Generator Pad</label>
          <input
            data-testid="field-distanceToGeneratorPad"
            type="number"
            inputMode="numeric"
            className={INPUT_CLASS}
            value={data.distanceToGeneratorPad ?? ''}
            onChange={(e) =>
              handleChange(
                'distanceToGeneratorPad',
                e.target.value === '' ? null : Number(e.target.value),
              )
            }
            placeholder="(ft)"
          />
        </div>
      </div>

      {/* Full-width fields */}
      <div>
        <label className={LABEL_CLASS}>Panel Location</label>
        <input
          type="text"
          className={INPUT_CLASS}
          value={data.panelLocation ?? ''}
          onChange={(e) => handleChange('panelLocation', e.target.value)}
          placeholder="e.g., Garage interior wall, basement"
        />
      </div>

      <div>
        <label className={LABEL_CLASS}>Existing Grounding Info</label>
        <textarea
          className={TEXTAREA_CLASS}
          rows={3}
          value={data.existingGroundingInfo ?? ''}
          onChange={(e) => handleChange('existingGroundingInfo', e.target.value)}
          placeholder="Describe existing grounding electrode system…"
        />
      </div>

      {/* Photos — only when the host supplies a capture panel */}
      {renderPhotoCapture && (
        <div>
          <div className="flex items-center gap-2 mb-3 px-1">
            <h3 className="text-[11px] font-black text-slate-500 uppercase tracking-[0.1em]">Photos</h3>
            <div className="flex-1 h-px bg-gradient-to-r from-slate-200 to-transparent" />
          </div>
          {renderPhotoCapture({
            section: 'electricalPanel',
            photos: data.photos,
            availableTags: [
              { value: 'panel_label', label: 'Panel Label' },
              { value: 'panel_interior', label: 'Panel Interior' },
            ],
          })}
        </div>
      )}
    </div>
  );
}
